import type { MouseEvent, TouchEvent, WheelEvent } from 'react';
import { X, Heart, Download, MessageCircle, Trash2, Eye, EyeOff, SquareArrowUp, SquareArrowDown, MoreHorizontal } from 'lucide-react';
import { CommentsPanel } from '@/components/CommentsPanel';
import { optimizeCloudinaryUrl, formatUploadTime, downloadImage } from '@/lib/utils';
import ClickableUserDisplay from '@/components/ClickableUserDisplay';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAlbumInteractions } from '@/contexts/AlbumInteractionsContext';
import type { MediaItem } from '../utils';
import { SLIDE_WINDOW_RADIUS } from '../constants';
import VotesPanel from './VotesPanel';

interface AlbumSlideshowProps {
  slideshowOpen: boolean;
  filteredAndSortedAssets: MediaItem[];
  loopedAssets: MediaItem[];
  pos: number;
  setPos: (value: number) => void;
  noTransition: boolean;
  setNoTransition: (value: boolean) => void;
  isDragging: boolean;
  dragOffset: number;
  isCommentsOpen: boolean;
  setIsCommentsOpen: (value: boolean) => void;
  isVotesOpen: boolean;
  setIsVotesOpen: (value: boolean) => void;
  setIsTransitioning: (value: boolean) => void;
  closeSlideshow: () => void;
  cameFromUsuario: boolean;
  navigateToUsuario: () => void;
  getAssetIndexLabel: string;
  slideshowIndex: number;
  isLikesEnabled: boolean;
  isCommentsEnabled: boolean;
  isDownloadEnabled: boolean;
  isDescriptionEnabled: boolean;
  isDescriptionExpanded: boolean;
  setIsDescriptionExpanded: (value: boolean) => void;
  user: { id: string } | null;
  userVotes: Set<string>;
  userCommentedMediaIds: Set<string>;
  currentLanguage: string;
  albumSlug: string;
  toast: (...args: unknown[]) => void;
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: (e: TouchEvent) => void;
  onMouseDown: (e: MouseEvent) => void;
  onMouseMove: (e: MouseEvent) => void;
  onMouseUp: () => void;
  onWheel: (e: WheelEvent) => void;
  onToggleVote: (mediaId: string, uploadedBy: string) => void;
  onCommentsCountChange: (delta: number, mediaId?: string) => void;
  showDeleteAction?: boolean;
  canDeleteCurrentAsset?: boolean;
  isDeletingCurrentAsset?: boolean;
  onRequestDeleteCurrentAsset?: () => void;
  showFlagAction?: boolean;
  canToggleCurrentAsset?: boolean;
  isTogglingFlag?: boolean;
  onToggleCurrentAssetFlag?: () => void;
  showFlaggedBanner?: boolean;
  t: (key: string) => string;
}

const AlbumSlideshow = ({
  slideshowOpen,
  filteredAndSortedAssets,
  loopedAssets,
  pos,
  setPos,
  noTransition,
  setNoTransition,
  isDragging,
  dragOffset,
  isCommentsOpen,
  setIsCommentsOpen,
  isVotesOpen,
  setIsVotesOpen,
  setIsTransitioning,
  closeSlideshow,
  cameFromUsuario,
  navigateToUsuario,
  getAssetIndexLabel,
  slideshowIndex,
  isLikesEnabled,
  isCommentsEnabled,
  isDownloadEnabled,
  isDescriptionEnabled,
  isDescriptionExpanded,
  setIsDescriptionExpanded,
  user,
  userVotes,
  userCommentedMediaIds,
  currentLanguage,
  albumSlug,
  toast,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onWheel,
  onToggleVote,
  onCommentsCountChange,
  showDeleteAction = false,
  canDeleteCurrentAsset = false,
  isDeletingCurrentAsset = false,
  onRequestDeleteCurrentAsset,
  showFlagAction = false,
  canToggleCurrentAsset = false,
  isTogglingFlag = false,
  onToggleCurrentAssetFlag,
  showFlaggedBanner = false,
  t,
}: AlbumSlideshowProps) => {
  const { getCommentsCount, getVotesCount, hasCurrentUserVoted, hasCurrentUserCommented } = useAlbumInteractions();
  const currentLogicalAsset = filteredAndSortedAssets[slideshowIndex] || null;
  const currentLoopedAsset = loopedAssets[pos] || null;
  const currentMediaId = currentLoopedAsset?.id || currentLogicalAsset?.id || null;

  if (!slideshowOpen || filteredAndSortedAssets.length === 0) {
    return null;
  }
  const canOpenOwnerActions = (showDeleteAction && canDeleteCurrentAsset) || (showFlagAction && canToggleCurrentAsset);

  return (
    <div
      id="gallery-slideshow"
      className='fixed inset-0 bg-black/90 z-[9999] overflow-hidden pointer-events-none md:pointer-events-auto cursor-pointer'
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingTop: 'env(safe-area-inset-top, 0px)' }}
      onClick={closeSlideshow}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (cameFromUsuario) {
            navigateToUsuario();
          } else {
            closeSlideshow();
          }
        }}
        className='pointer-events-auto absolute right-6 z-50 text-white p-2 hover:bg-white/20 rounded-full transition-colors'
        style={{ top: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}
      >
        <X className='w-6 h-6' />
      </button>

      <div className="pointer-events-auto w-full h-full md:max-w-[450px] mx-auto relative" onClick={(e) => { e.stopPropagation(); if (isCommentsOpen) setIsCommentsOpen(false); if (isVotesOpen) setIsVotesOpen(false); }}>
        <div
          className="w-full h-full relative"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseMove={isDragging ? onMouseMove : undefined}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onWheel={onWheel}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            overscrollBehavior: 'contain'
          }}
        >
          <div
            className={`flex flex-col ${noTransition || isDragging ? '' : 'transition-transform duration-200 ease-out'}`}
            style={{
              transform: `translateY(calc(-${pos * 100}svh + ${isDragging ? -dragOffset : 0}px))`,
              height: `${loopedAssets.length * 100}svh`
            }}
            onTransitionEnd={(e) => {
              if (e.propertyName !== 'transform') return;
              if (loopedAssets.length <= 2) return;
              if (pos === 0) {
                setNoTransition(true);
                const target = loopedAssets.length - 2;
                setPos(target);
                requestAnimationFrame(() =>
                  requestAnimationFrame(() => setNoTransition(false))
                );
              } else if (pos === loopedAssets.length - 1) {
                setNoTransition(true);
                setPos(1);
                requestAnimationFrame(() =>
                  requestAnimationFrame(() => setNoTransition(false))
                );
              }
              setIsTransitioning(false);
            }}
          >
            {loopedAssets.map((asset, i) => {
              const startIdx = Math.max(0, pos - SLIDE_WINDOW_RADIUS);
              const endIdx = Math.min(loopedAssets.length, pos + SLIDE_WINDOW_RADIUS);

              if (i < startIdx || i >= endIdx) {
                return (
                  <div
                    key={`slide-placeholder-${i}`}
                    className="w-full h-[100svh] flex-shrink-0 bg-black"
                  />
                );
              }

              const isCurrent = i === pos;
              const isVotedByCurrentUser = hasCurrentUserVoted(asset.id, userVotes.has(asset.id));
              const isCommentedByCurrentUser = hasCurrentUserCommented(asset.id, userCommentedMediaIds.has(asset.id));
              const votesCount = getVotesCount(asset.id, asset.votes_count ?? 0);
              const commentsCount = getCommentsCount(asset.id, asset.comments_count ?? 0);
              const displayM = asset.cdn_display_m || null;
              const displayS = asset.cdn_display_s || null;
              const cloudFallback = optimizeCloudinaryUrl({
                originalUrl: asset.secure_url,
                width: 1200,
              });
              const slideshowUrl = displayM || displayS || cloudFallback;

              return (
                <div
                  key={`slide-${i}-${asset.id}`}
                  className="relative w-full h-[100svh] flex items-center justify-center flex-shrink-0 bg-black"
                >
                  <img
                    src={slideshowUrl}
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      if (displayM && displayS) {
                        img.src = displayS;
                        return;
                      }
                      img.src = cloudFallback;
                    }}
                    alt={`Asset ${i + 1}`}
                    className='max-w-full max-h-full w-auto h-auto object-contain pointer-events-none'
                  />

                  {isCurrent && (
                    <>
                      {showFlaggedBanner && asset.flagged && (
                        <div
                          className="pointer-events-none absolute top-4 left-4 right-4 z-10 rounded-full border border-red-500/70 bg-red-500/90 px-4 py-2 text-center text-sm font-semibold text-white"
                          style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
                        >
                          {t('notPubliclyVisible')}
                        </div>
                      )}
                      <div
                        className="pointer-events-auto absolute right-4 flex flex-col items-center space-y-6  z-10"
                        style={{ bottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}
                      >
                        {isLikesEnabled && (
                          user && asset.uploaded_by === user.id ? (
                            <div className="pointer-events-auto relative">
                              <div className="flex flex-col items-center space-y-1 text-white">
                                <button
                                  aria-label={t('votes')}
                                  className="text-white"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsCommentsOpen(false);
                                    setIsVotesOpen(true);
                                  }}
                                >
                                  <div className="relative">
                                    <Heart className={`w-8 h-8 ${isVotedByCurrentUser ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                                  </div>
                                </button>
                                <button
                                  aria-label={t('votes')}
                                  className="text-xs font-semibold text-white"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsCommentsOpen(false);
                                    setIsVotesOpen(true);
                                  }}
                                >
                                  {votesCount}
                                </button>
                              </div>
                            </div>
                          ) : user ? (
                            <div className="flex flex-col items-center space-y-1 text-white">
                              <button
                                aria-label={t('likes')}
                                className="text-white"
                                onClick={() => onToggleVote(asset.id, asset.uploaded_by || '')}
                              >
                                <div className="relative">
                                  <Heart className={`w-8 h-8 ${isVotedByCurrentUser ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                                </div>
                              </button>
                              <button
                                aria-label={t('votes')}
                                className="text-xs font-semibold text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsCommentsOpen(false);
                                  setIsVotesOpen(true);
                                }}
                              >
                                {votesCount}
                              </button>
                            </div>
                          ) : (
                            votesCount > 0 && (
                              <div className="flex flex-col items-center space-y-1 text-white">
                                <div className="relative">
                                  <Heart className="w-8 h-8 text-white" />
                                </div>
                                <span className="text-xs font-semibold">{votesCount}</span>
                              </div>
                            )
                          )
                        )}

                        {isCommentsEnabled && (
                          <button
                            className="flex flex-col items-center space-y-1 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsVotesOpen(false);
                              setIsCommentsOpen(true);
                            }}
                          >
                            <MessageCircle className={`w-8 h-8 text-white ${isCommentedByCurrentUser ? 'fill-white' : ''}`} />
                            <span className="text-xs font-semibold text-white">
                              {commentsCount}
                            </span>
                          </button>
                        )}

                        {isDownloadEnabled && user && (
                          <button
                            className="flex flex-col items-center space-y-1 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadImage({ imageUrl: asset.cdn_original || asset.secure_url, albumSlug: albumSlug, fileName: `${albumSlug}-foto-ID_${asset.id.substring(0, 5)}.jpg`, toast, userId: user.id });
                            }}
                          >
                            <Download className="w-8 h-8" />
                          </button>
                        )}
                        {canOpenOwnerActions && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="flex flex-col items-center space-y-1 text-white"
                                onClick={(e) => e.stopPropagation()}
                                aria-label={t('actions')}
                              >
                                <MoreHorizontal className="w-8 h-8" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              side="left"
                              align="end"
                              className="z-[10020] min-w-[170px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {showDeleteAction && canDeleteCurrentAsset && (
                                <DropdownMenuItem
                                  onSelect={() => onRequestDeleteCurrentAsset?.()}
                                  disabled={isDeletingCurrentAsset}
                                  className="gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>{t('deleteShort')}</span>
                                </DropdownMenuItem>
                              )}

                              {showFlagAction && canToggleCurrentAsset && (
                                <DropdownMenuItem
                                  onSelect={() => onToggleCurrentAssetFlag?.()}
                                  disabled={isTogglingFlag}
                                  className="gap-2"
                                >
                                  {asset.flagged ? (
                                    <EyeOff className="w-4 h-4 text-red-500" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                  <span>{asset.flagged ? t('show') : t('hide')}</span>
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        <div id="user-image" className="flex flex-col items-center space-y-1 text-white">
                          <ClickableUserDisplay
                            userId={asset.uploaded_by}
                            displayName={asset.user_name || 'Un invitado'}
                            avatarUrl={asset.user_avatar}
                            variant="regular"
                            className="text-white"
                            avatarClassName="border-2 border-white"
                            nameClassName="hidden"
                          />
                        </div>
                      </div>

                      <div className=" bg-gradient-to-t from-black to-transparent absolute bottom-0 left-0 w-full h-1/5 pointer-events-none"></div>
                      <div
                        className="absolute left-4 right-20 text-white z-10"
                        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
                      >
                        <div className="flex items-center mb-2">
                          <ClickableUserDisplay
                            userId={asset.uploaded_by}
                            displayName={asset.user_name || 'Un invitado'}
                            avatarUrl={asset.user_avatar}
                            showAvatar={false}
                            variant="compact"
                            className="text-white"
                            nameClassName="font-semibold text-white"
                          />
                        </div>

                        {isDescriptionEnabled && asset.description ? (
                          <div className="text-sm text-gray-200">
                            <p className={`${!isDescriptionExpanded ? 'line-clamp-2' : ''} whitespace-pre-wrap break-words`}>
                              {asset.description}
                            </p>
                            {asset.created_at && (isDescriptionExpanded || (asset.description?.length ?? 0) <= 100) && (
                              <div className="text-xs text-gray-300 mt-1">
                                {formatUploadTime(asset.created_at, currentLanguage)}
                              </div>
                            )}
                            {asset.description.length > 100 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setIsDescriptionExpanded(!isDescriptionExpanded); }}
                                className="text-primary font-medium hover:underline"
                              >
                                {isDescriptionExpanded ? t('less') : `... ${t('more')}`}
                              </button>
                            )}
                          </div>
                        ) : (
                          asset.created_at ? (
                            <div className="text-xs text-gray-300 mt-1">
                              {formatUploadTime(asset.created_at, currentLanguage)}
                            </div>
                          ) : null
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="absolute left-6 bg-black/50 rounded-full px-3 py-1 text-white text-sm"
        style={{ top: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}
      >
        {getAssetIndexLabel}
      </div>

      {isCommentsEnabled && (
        <CommentsPanel
          mediaId={currentMediaId}
          isOpen={isCommentsOpen}
          onClose={() => setIsCommentsOpen(false)}
          onCountChange={(delta) => onCommentsCountChange(delta, currentMediaId || undefined)}
        />
      )}
      {isLikesEnabled && (
        <VotesPanel
          mediaId={currentMediaId}
          isOpen={isVotesOpen}
          onClose={() => setIsVotesOpen(false)}
        />
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-6 right-6 hidden lg:flex items-center gap-2 text-white/80"
      >
        <SquareArrowUp className="h-7 w-7" />
        <SquareArrowDown className="h-7 w-7" />
      </div>
    </div>
  );
};

export default AlbumSlideshow;
